/**
 * Workspace invite link calls (SC-438 D14). See `cloud/companyInviteLinkManager.js`.
 */
import { parseCloudRun } from './parseCloud'

export interface CompanyInviteLinkState {
  token: string | null
  active: boolean
  /**
   * The role the link was given, or null when it follows the default. Cloud that predates link
   * roles sends neither this nor `effectiveRoleKey`: read that as "the default".
   */
  roleKey?: string | null
  /** The role people joining with this link get now (the link's, else the company default chain). */
  effectiveRoleKey?: string
  /**
   * True when the link is on but its token is withheld (`token` is null) because the viewer could
   * not assign the role the link gives. Cloud that predates it sends no field: read that as false.
   */
  tokenHidden?: boolean
}

export interface CompanyInviteLinkPreview {
  companyName: string
  logoUrl: string | null
}

/** The path the join page answers on. */
export const JOIN_COMPANY_PATH = '/join-company'

export function companyInviteLinkUrl(token: string, origin: string = window.location.origin): string {
  return `${origin}${JOIN_COMPANY_PATH}?token=${encodeURIComponent(token)}`
}

/**
 * The active link; `create: false` only reads it, without making one. `role` (a role key, or a
 * legacy string) is used only when this call has to create the link.
 */
export const getCompanyInviteLink = (params: { companyId: string; create?: boolean; role?: string }) =>
  parseCloudRun<CompanyInviteLinkState>('getCompanyInviteLink', {
    companyId: params.companyId,
    create: params.create ?? true,
    ...(params.role ? { role: params.role } : {}),
  })

/** A new token; without `role` the new link keeps the old link's role. */
export const resetCompanyInviteLink = (params: { companyId: string; role?: string }) =>
  parseCloudRun<CompanyInviteLinkState>('resetCompanyInviteLink', {
    companyId: params.companyId,
    ...(params.role ? { role: params.role } : {}),
  })

/** Changes who the link makes people, keeping the same token; `role: null` follows the default. */
export const updateCompanyInviteLinkRole = (params: { companyId: string; role: string | null }) =>
  parseCloudRun<CompanyInviteLinkState>('updateCompanyInviteLinkRole', {
    companyId: params.companyId,
    role: params.role,
  })

export const disableCompanyInviteLink = (companyId: string) =>
  parseCloudRun<CompanyInviteLinkState>('disableCompanyInviteLink', { companyId })

export const previewCompanyInviteLink = (token: string) =>
  parseCloudRun<CompanyInviteLinkPreview>('previewCompanyInviteLink', { token })

export const joinCompanyByInviteLink = (token: string) =>
  parseCloudRun<{ success: boolean; companyId: string; alreadyMember: boolean }>('joinCompanyByInviteLink', { token })

/** The cloud prefixes messages with a code (e.g. `INVITE_LINK_OFF: …`); people see only the text. */
export function inviteLinkErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : String((err as { message?: string })?.message || '')
  return message.replace(/^[A-Z_]+:\s*/, '') || 'Something went wrong. Try again.'
}

const PENDING_KEY = 'sevv.pendingCompanyInvite'
/** A saved link is used within a day; an old one is dropped rather than joining someone much later. */
const PENDING_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Keeps the token while a signed-out visitor signs up or signs in. The email code and Google
 * sign-in both leave the join page, so the URL alone would lose it.
 *
 * Session storage, not local: it stays with the tab that opened the link (through the Google
 * redirect and the email code), so whoever signs in next in another tab or browser session on a
 * shared computer is never joined to the company by someone else's link.
 */
export function savePendingCompanyInvite(token: string, companyName = '', now = Date.now()): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ token, companyName, savedAt: now }))
  } catch {
    // Storage blocked: the visitor can open the link again once signed in.
  }
}

export interface PendingCompanyInvite {
  token: string
  /** For the sign-in screens to say which company the visitor is joining; '' if unknown. */
  companyName: string
}

function readPendingCompanyInvite(now: number): PendingCompanyInvite | null {
  const raw = sessionStorage.getItem(PENDING_KEY)
  if (!raw) return null
  const { token, companyName, savedAt } = JSON.parse(raw) as {
    token?: string
    companyName?: string
    savedAt?: number
  }
  if (!token || typeof savedAt !== 'number' || now - savedAt >= PENDING_TTL_MS) return null
  return { token, companyName: typeof companyName === 'string' ? companyName : '' }
}

/** The saved invite, left in place, or null when there is none or it is over a day old. */
export function peekPendingCompanyInvite(now = Date.now()): PendingCompanyInvite | null {
  try {
    return readPendingCompanyInvite(now)
  } catch {
    return null
  }
}

/** The saved token, removed as it is read, or null when there is none or it is over a day old. */
export function takePendingCompanyInvite(now = Date.now()): string | null {
  try {
    return readPendingCompanyInvite(now)?.token ?? null
  } catch {
    return null
  } finally {
    clearPendingCompanyInvite()
  }
}

/** Drops a saved token unused, on sign-out, so the next account to sign in isn't joined with it. */
export function clearPendingCompanyInvite(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY)
  } catch {
    // Storage blocked: nothing was saved.
  }
}
